// src/pages/Shop.jsx — v6
// CHANGED FROM v5 (1 Aug 2026): passes priority={true} to the first 4
// cards in the results grid — real LCP fix, see ProductCard.jsx v25.
// CHANGED FROM v4 (31 Jul 2026): "New in" filter chip given its own
// dedicated class (filter-chip-newin) so it can be styled distinctly
// from the other filter chips, per Amit's request that it "visually
// register more" without reintroducing the other lifecycle chips
// (Hot Selling / Almost Sold Out) that were deliberately removed in
// an earlier cleanup — only this one chip's own look changes, nothing
// else. Styling added via a new inline <style> block at the bottom of
// this file (Shop.jsx previously had zero inline styles, relying only
// on the external Shop.css — this is additive, Shop.css untouched).
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import BottomNav from '../components/BottomNav';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../hooks/useProducts';
import './Shop.css';
const TOP_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'new', label: 'New in' },
  { id: 'garments', label: 'Garments' },
  { id: 'jewellery', label: 'Jewellery' },
  { id: 'watches', label: 'Watches' },
  { id: 'bags', label: 'Bags' },
  { id: 'shoes', label: 'Shoes' },
  { id: 'under999', label: 'Under ₹999' },
];
const NEW_WINDOW_DAYS = 3;
const SORTS = ['Popular', 'Price: Low to high', 'Price: High to low', 'Newest'];
export default function Shop() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState(() => {
    const cat = searchParams.get('cat');
    const tag = searchParams.get('tag');
    return cat || tag || sessionStorage.getItem('lastFilter') || 'all';
  });
  const [activeSubFilter, setActiveSubFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(() => sessionStorage.getItem('lastSort') || 'Popular');
  const [showSort, setShowSort] = useState(false);
  const { products, loading } = useProducts();
  useEffect(() => {
    const cat = searchParams.get('cat');
    const tag = searchParams.get('tag');
    if (cat || tag) {
      const filter = cat || tag;
      setActiveFilter(filter);
      setActiveSubFilter('all');
      sessionStorage.setItem('lastFilter', filter);
    }
  }, [searchParams]);
  function handleFilterClick(filterId) {
    setActiveFilter(filterId);
    setActiveSubFilter('all');
    sessionStorage.setItem('lastFilter', filterId);
  }
  const garmentSubCats = useMemo(() => {
    if (activeFilter !== 'garments') return [];
    const cats = [...new Set(
      products
        .filter(p => p.category === 'Garments' && p.subCategory)
        .map(p => p.subCategory)
    )].sort();
    return cats;
  }, [products, activeFilter]);
  const filtered = useMemo(() => {
    const newCutoff = new Date();
    newCutoff.setDate(newCutoff.getDate() - NEW_WINDOW_DAYS);
    let list = products.filter(p => {
      const searchLower = search.toLowerCase();
      const matchSearch = p.name.toLowerCase().includes(searchLower) ||
        p.id.toLowerCase().includes(searchLower);
      const matchFilter =
        activeFilter === 'all' ? true :
        activeFilter === 'garments' ? p.category === 'Garments' :
        activeFilter === 'jewellery' ? p.category === 'Jewellery' :
        activeFilter === 'watches' ? p.category === 'Watches' :
        activeFilter === 'bags' ? p.category === 'Bags' :
        activeFilter === 'shoes' ? p.category === 'Shoes' :
        activeFilter === 'under999' ? p.price < 999 :
        activeFilter === 'new' ? (p.updateDate && p.updateDate >= newCutoff) :
        true;
      const matchSub = activeSubFilter === 'all' ? true : p.subCategory === activeSubFilter;
      return matchSearch && matchFilter && matchSub;
    });
    if (sort === 'Price: Low to high') list = [...list].sort((a, b) => a.price - b.price);
    if (sort === 'Price: High to low') list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [products, search, activeFilter, activeSubFilter, sort]);
  return (
    <div className="app-shell">
      <Navbar />
      <div className="shop-header">
        <div className="shop-title">All products</div>
        <div className="shop-count">{products.length} products available</div>
        <div className="search-bar">
          <i className="ti ti-search" aria-hidden="true"></i>
          <input
            type="text"
            placeholder="Search garments, jewellery…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search products"
          />
          {search && (
            <button onClick={() => setSearch('')} aria-label="Clear search">
              <i className="ti ti-x" style={{ fontSize: 16, color: 'var(--muted)' }}></i>
            </button>
          )}
        </div>
        <div className="filter-row" role="group" aria-label="Filter products">
          {TOP_FILTERS.map(f => (
            <button
              key={f.id}
              className={`filter-chip ${f.id === 'new' ? 'filter-chip-newin' : ''} ${activeFilter === f.id ? 'active' : ''}`}
              onClick={() => handleFilterClick(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        {activeFilter === 'garments' && garmentSubCats.length > 0 && (
          <div className="filter-row sub-filter-row" role="group" aria-label="Sub-category filter">
            <button
              className={`filter-chip sub-chip ${activeSubFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveSubFilter('all')}
            >
              All Garments
            </button>
            {garmentSubCats.map(cat => (
              <button
                key={cat}
                className={`filter-chip sub-chip ${activeSubFilter === cat ? 'active' : ''}`}
                onClick={() => setActiveSubFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="sort-row">
        <span className="result-count">{filtered.length} products</span>
        <div style={{ position: 'relative' }}>
          <button className="sort-btn" onClick={() => setShowSort(!showSort)}>
            <i className="ti ti-arrows-sort" aria-hidden="true"></i> {sort}
          </button>
          {showSort && (
            <div className="sort-dropdown">
              {SORTS.map(s => (
                <button
                  key={s}
                  className={`sort-option ${sort === s ? 'active' : ''}`}
                  onClick={() => { setSort(s); sessionStorage.setItem('lastSort', s); setShowSort(false); }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="shop-grid-wrap">
        {loading ? (
          <div className="prod-grid">
            {[...Array(6)].map((_, i) => <div key={i} className="prod-skeleton"></div>)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <i className="ti ti-search" style={{ fontSize: 40, color: 'var(--muted)', marginBottom: 12 }}></i>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>No products found</div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Try a different search or filter</div>
          </div>
        ) : (
          <div className="prod-grid">
            {filtered.map((p, i) => <ProductCard key={p.id} product={p} priority={i < 4} />)}
          </div>
        )}
      </div>
      <BottomNav />
      <style>{`
        .filter-chip-newin {
          border-color: #D4A020 !important;
          color: #D4A020 !important;
          font-weight: 600;
        }
        .filter-chip-newin.active {
          background: #D4A020 !important;
          color: #1C1A17 !important;
          border-color: #D4A020 !important;
        }
      `}</style>
    </div>
  );
}